import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { Logo } from '@/components/Logo'
import { MagneticButton } from '@/components/MagneticButton'
import { ParticipationNote } from '@/components/lc'
import { PlaceholderEvents } from '@/components/PlaceholderEvents'
import { PinnedCardRail, RailHeading } from '@/components/ui/PinnedCardRail'
import { gsap, useGSAP } from '@/lib/gsap'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { cn } from '@/lib/utils'
import { howItWorks, cosmos, cosmosMentors, communityPages, events, programs, trust, brand, partnerWhy, hostPhotoFor } from '@/data/content'
import type { IconName, StepAudience } from '@/data/content'
import { communityWebappUrl, eventWebappUrl } from '@/lib/auth'
import { useRecentEvents, type DisplayEvent } from '@/lib/events'

/* ================================================================== */
/*  Sections for the new hero bases (dark + light themes):             */
/*  1. HowItWorksStack: Option D's content in the A+B sticky-stacking  */
/*     interaction (cards pin and hand off as you scroll).             */
/*  2. PoppingMessages: a divider graphic of chat bubbles popping in   */
/*     one by one, on-brand copy.                                      */
/*  3. FeatureBlocks: an interactive bento (events, daily practice,    */
/*     circle of peers, LifeCrafters) with hover micro-interactions.   */
/* ================================================================== */

type ThemeProps = { dark?: boolean }

const glass = (_dark: boolean) => 'lc-glass'
const glassSoft = (_dark: boolean) => 'lc-glass-soft'
const inkText = (_dark: boolean) => 'text-lc-ink'
const muteText = (_dark: boolean) => 'text-lc-mute'

/* phones can't scroll-jack comfortably; several sections swap their pinned
   interactions for a plain swipe. One shared matchMedia hook drives that. */
function useIsMobile() {
  const [mobile, setMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const on = () => setMobile(mq.matches)
    on()
    if (mq.addEventListener) mq.addEventListener('change', on)
    else mq.addListener(on) // Safari < 14 fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on)
      else mq.removeListener(on)
    }
  }, [])
  return mobile
}

/* ------------------------------------------------------------------ */
/*  1 · HOW IT WORKS: "why are you here?" picker. The visitor chooses   */
/*  their path (join vs host) and gets that path's three steps + CTA.   */
/* ------------------------------------------------------------------ */
export function HowItWorksStack({ dark = true }: ThemeProps) {
  const [path, setPath] = useState<StepAudience>('explorer')
  const steps = howItWorks.filter((s) => s.audience === path)

  const options: { key: StepAudience; short: string; long: string }[] = [
    { key: 'explorer', short: 'Join a community', long: 'I want to learn, belong and take part' },
    { key: 'lifecrafter', short: 'Host & teach', long: 'I want to share my craft and earn' },
  ]

  return (
    <section id="next" className="relative px-5 py-[clamp(3rem,2rem+4vw,5rem)]">
      <div className="container-lc">
        <div className="text-center">
          <p className={cn('label', muteText(dark))}>How it works</p>
          <h2 className={cn('mx-auto mt-4 max-w-[16ch] font-display text-[clamp(1.6rem,1.05rem+2.2vw,3.4rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
            What brings you here?
          </h2>
          {/* path picker: two real tabs on one line */}
          <div className={cn('mt-8 inline-flex flex-row gap-1.5 rounded-pill p-1.5', glass(dark))} role="tablist" aria-label="Choose your path">
            {options.map((o) => {
              const active = path === o.key
              return (
                <button
                  key={o.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setPath(o.key)}
                  className={cn(
                    'whitespace-nowrap rounded-pill px-5 py-2.5 text-center text-[14px] font-bold transition-colors duration-300 sm:text-[15px]',
                    active ? 'bg-lime text-night' : cn('hover:bg-white/5', inkText(dark)),
                  )}
                >
                  {o.short}
                </button>
              )
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={path}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="mt-10"
          >
            <div className="grid gap-4 md:grid-cols-3">
              {steps.map((s) => (
                <article key={s.audience + s.n} className={cn('relative overflow-hidden rounded-[28px] p-5 xl:p-7', glass(dark))}>
                  <div className="flex items-center justify-between">
                    <span className={cn('grid h-12 w-12 place-items-center rounded-2xl', glassSoft(dark), inkText(dark))}>
                      <Icon name={s.icon} size={22} />
                    </span>
                    <span className={cn('font-display text-[2.6rem] font-normal leading-none', muteText(dark))}>{s.n}</span>
                  </div>
                  <h3 className={cn('mt-6 font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>{s.title}</h3>
                  <p className={cn('mt-3 text-[15px] xl:text-[16px] leading-relaxed', muteText(dark))}>{s.body}</p>
                </article>
              ))}
            </div>

            <div className="mt-10 text-center">
              {path === 'explorer' ? (
                <>
                  <MagneticButton
                    href="/communities"
                    data-cursor="join"
                    className="inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[16px] font-bold text-night"
                  >
                    Explore communities →
                  </MagneticButton>
                  <p className={cn('label mt-4', muteText(dark))}>Free to join. Your people are already here.</p>
                </>
              ) : (
                <>
                  <MagneticButton
                    href="/lifecrafter-program"
                    data-cursor="apply"
                    className="inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[16px] font-bold text-night"
                  >
                    Become a LifeCrafter →
                  </MagneticButton>
                  <p className={cn('label mt-4', muteText(dark))}>Verified hosts only. Payments &amp; payouts handled.</p>
                </>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  2 · POPPING MESSAGES: divider graphic, bubbles pop in one by one   */
/* ------------------------------------------------------------------ */
type Bubble = {
  text: string
  who: string
  x: number // % across the band
  y: number // % down the band
  tone: 'violet' | 'coral' | 'glassy'
  align?: 'left' | 'right'
}

const BUBBLES: Bubble[] = [
  { text: 'Same time next full moon?', who: 'Zoin circle', x: 12, y: 16, tone: 'glassy' },
  { text: 'Meera. That riyaaz. Goosebumps.', who: 'Vikram · LifeCrafter', x: 60, y: 8, tone: 'violet', align: 'right' },
  { text: 'Photo walk, 6am. Chai’s on me.', who: 'Lenscraft', x: 30, y: 44, tone: 'coral' },
  { text: 'My knees showed up. My excuses didn’t.', who: 'Forever Fit', x: 66, y: 52, tone: 'glassy', align: 'right' },
  { text: 'Saturn return club, assemble.', who: 'Cosmos', x: 10, y: 74, tone: 'violet' },
  { text: 'Front row at your debut. No arguments.', who: 'The Stage', x: 55, y: 82, tone: 'coral', align: 'right' },
]

export function PoppingMessages({ dark = true }: ThemeProps) {
  const scope = useRef<HTMLElement>(null)
  const reduced = usePrefersReducedMotion()

  useGSAP(() => {
    if (reduced || !scope.current) return
    const bubbles = gsap.utils.toArray<HTMLElement>('.pm-bubble', scope.current)
    gsap.set(bubbles, { scale: 0.4, opacity: 0, transformOrigin: '20% 100%' })
    const tl = gsap.timeline({
      scrollTrigger: { trigger: scope.current, start: 'top 70%', once: true },
    })
    tl.to(bubbles, { scale: 1, opacity: 1, duration: 0.55, ease: 'back.out(2)', stagger: 0.16 })
    return () => { tl.scrollTrigger?.kill(); tl.kill() }
  }, { scope, dependencies: [reduced] })

  const toneCls = (b: Bubble) => {
    if (b.tone === 'violet') return 'bg-violet text-white'
    if (b.tone === 'coral') return 'bg-coral text-white'
    return cn(glassSoft(dark), inkText(dark))
  }

  return (
    <section ref={scope} className="relative px-5 py-[10vh]" aria-label="Overheard on LifeCraft4U">
      <p className={cn('label text-center', muteText(dark))}>Overheard on LifeCraft4U</p>
      <div className="relative mx-auto mt-8 h-[46vh] min-h-[340px] w-full max-w-5xl">
        {BUBBLES.map((b) => (
          <figure
            key={b.text}
            className="pm-bubble absolute max-w-[280px] motion-safe:animate-float"
            style={{ left: `${b.x}%`, top: `${b.y}%`, animationDelay: `${(b.x + b.y) % 5}s`, animationDuration: `${6 + (b.y % 3)}s` }}
          >
            <blockquote
              className={cn(
                'rounded-[20px] px-5 py-3 text-[15px] font-medium leading-snug shadow-lg',
                b.align === 'right' ? 'rounded-br-md' : 'rounded-bl-md',
                toneCls(b),
              )}
            >
              {b.text}
            </blockquote>
            <figcaption className={cn('label mt-2 !text-[11px]', b.align === 'right' ? 'text-right' : '', muteText(dark))}>
              {b.who}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  3 · FEATURE BLOCKS: interactive bento                              */
/* ------------------------------------------------------------------ */
export function FeatureBlocks({ dark = true }: ThemeProps) {
  const avatars = ['/img/portrait-1.jpg', '/img/portrait-3.jpg', '/img/portrait-2.jpg', '/img/portrait-5.jpg']

  return (
    <section className="relative px-5 py-[12vh]">
      <div className="container-lc">
        <div className="mb-10 text-center">
          <p className={cn('label', muteText(dark))}>What you get</p>
          <h2 className={cn('mx-auto mt-4 max-w-[20ch] font-display text-[clamp(2rem,1.2rem+3.4vw,3.8rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
            Built for you. Built with trust.
          </h2>
        </div>

        <div className="grid gap-4 md:grid-cols-6">
          {/* Live events, wide block */}
          <div className={cn('group relative overflow-hidden rounded-[28px] p-5 xl:p-7 transition-transform duration-300 hover:-translate-y-1 md:col-span-4', glass(dark))}>
            <span className="blob absolute -right-12 -top-12 h-44 w-44 opacity-30" style={{ background: '#C8F04B' }} aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <span className={cn('grid h-12 w-12 place-items-center rounded-2xl', glassSoft(dark), inkText(dark))}><Icon name="calendar" size={22} /></span>
                <span className={cn('label', muteText(dark))}>Live events</span>
              </div>
              <h3 className={cn('mt-6 font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>Show up, online or near you.</h3>
              <div className={cn('mt-5 rounded-2xl p-4', glassSoft(dark))}>
                <div className="flex items-center justify-between gap-3">
                  <span className={cn('truncate text-[15px] font-medium', inkText(dark))}>Full-Moon Sit · Thursday, 8:30</span>
                  <span className={cn('label shrink-0 rounded-pill px-2 py-0.5 !text-[10px]', dark ? 'bg-white/10 text-white/70' : 'bg-black/5 text-lc-mute')}>Example</span>
                </div>
                <p className={cn('mt-1.5 text-[12px] leading-relaxed', muteText(dark))}>An illustration of the kind of gatherings hosted, not a live listing.</p>
              </div>
              <p className={cn('mt-4 text-[15px] xl:text-[16px] leading-relaxed', muteText(dark))}>A trustworthy “what’s on this month”: verified hosts, easy RSVP, zero spam.</p>
            </div>
          </div>

          {/* Daily practice */}
          <div className={cn('group relative overflow-hidden rounded-[28px] p-5 xl:p-7 transition-transform duration-300 hover:-translate-y-1 md:col-span-2', glass(dark))}>
            <span className="blob absolute -left-12 -top-12 h-40 w-40 opacity-30" style={{ background: '#7C3AED' }} aria-hidden="true" />
            <div className="relative">
              <span className={cn('grid h-12 w-12 place-items-center rounded-2xl transition-transform duration-700 group-hover:rotate-[40deg]', glassSoft(dark), inkText(dark))}>
                <Icon name="sun" size={22} />
              </span>
              <h3 className={cn('mt-6 font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>A daily practice.</h3>
              <p className={cn('mt-3 text-[15px] xl:text-[16px] leading-relaxed', muteText(dark))}>A calm, dated “Today” that is yours. A ritual to return to, never a streak to protect.</p>
              <div className={cn('mt-5 max-h-0 overflow-hidden rounded-2xl px-4 transition-all duration-500 group-hover:max-h-24 group-hover:py-3', glassSoft(dark))}>
                <p className={cn('label !text-[11px]', muteText(dark))}>Today · Rhythm · example</p>
                <p className={cn('mt-1 text-[15px] font-medium', inkText(dark))}>Sing one raag before chai.</p>
              </div>
            </div>
          </div>

          {/* A space to share: your circles are a social home, not a booking queue */}
          <div className={cn('group relative overflow-hidden rounded-[28px] p-5 xl:p-7 transition-transform duration-300 hover:-translate-y-1 md:col-span-3', glass(dark))}>
            <span className="blob absolute -bottom-12 -left-12 h-40 w-40 opacity-30" style={{ background: '#FF6B5C' }} aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <div className="flex h-12 shrink-0 items-center">
                  {avatars.slice(0, 3).map((a, i) => (
                    <img
                      key={a}
                      src={a}
                      alt=""
                      className={cn('h-11 w-11 rounded-full object-cover ring-2 transition-transform duration-500', dark ? 'ring-night/70' : 'ring-white', i > 0 && '-ml-4')}
                      style={{ transitionDelay: `${i * 40}ms` }}
                      data-fan={i}
                    />
                  ))}
                </div>
                <h3 className={cn('font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>A space to share.</h3>
              </div>
              <p className={cn('mt-3 text-[15px] xl:text-[16px] leading-relaxed', muteText(dark))}>Post your thoughts, ideas and progress. A place you belong, not just a place you book.</p>
              <div className={cn('mt-4 rounded-2xl px-4 py-3', glassSoft(dark))}>
                <p className={cn('label !text-[11px]', muteText(dark))}>you · Rhythm circle · just now</p>
                <p className={cn('mt-1 text-[15px] font-medium', inkText(dark))}>Day 12 of riyaaz. First raag without stopping.</p>
                <p className="label mt-2 !text-[11px] text-lime opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  ♥ 24 · 6 replies · Vikram clapped
                </p>
              </div>
            </div>
            {/* fan the avatars out on hover */}
            <style>{`
              .group:hover [data-fan="1"] { transform: translateX(6px); }
              .group:hover [data-fan="2"] { transform: translateX(12px); }
              .group:hover [data-fan="3"] { transform: translateX(18px); }
            `}</style>
          </div>

          {/* Real LifeCrafters */}
          <div className={cn('group relative overflow-hidden rounded-[28px] p-5 xl:p-7 transition-transform duration-300 hover:-translate-y-1 md:col-span-3', glass(dark))}>
            <span className="blob absolute -bottom-14 -right-12 h-44 w-44 opacity-30" style={{ background: '#7C3AED' }} aria-hidden="true" />
            <div className="relative">
              <div className="flex items-center gap-3">
                <span className={cn('grid h-12 w-12 shrink-0 place-items-center rounded-2xl', glassSoft(dark), inkText(dark))}><Icon name="shield" size={22} /></span>
                <h3 className={cn('font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>Real LifeCrafters.</h3>
              </div>
              <p className={cn('mt-3 text-[15px] xl:text-[16px] leading-relaxed', muteText(dark))}>Vetted, identity-verified hosts who reply to you. Personally. Not an algorithm, not a pedestal.</p>
              <div className="mt-4">
                <div className={cn('rounded-[18px] rounded-bl-md px-4 py-3', glassSoft(dark))}>
                  <p className={cn('label !text-[11px]', muteText(dark))}>you · 2m</p>
                  <p className={cn('text-[15px]', inkText(dark))}>Sent my first recording. Terrified.</p>
                </div>
                <div className="max-h-0 overflow-hidden rounded-[18px] rounded-br-md bg-violet px-4 text-white opacity-0 transition-all duration-500 group-hover:mt-2 group-hover:max-h-28 group-hover:py-3 group-hover:opacity-100">
                  <p className="label !text-[11px] text-white/70">Vikram · LifeCrafter · just now</p>
                  <p className="text-[15px]">Terrified sounds right. Send the next one tomorrow.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <MagneticButton
            href="/communities"
            data-cursor="join"
            className={cn('rounded-pill px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[16px] font-bold', dark ? 'bg-lime text-night' : 'bg-ink text-cream')}
          >
            Explore communities
          </MagneticButton>
          <ParticipationNote className="mt-6 mx-auto text-center" />
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  4 · LIFECRAFTER RING: a 3D photo drum (Vogue/Archrival style).     */
/*  Rotates continuously; the whole drum TILTS with the mouse; hover   */
/*  a face and it grows while the rotation keeps going. Large mixed    */
/*  typography below, then the trust and safety details.               */
/* ------------------------------------------------------------------ */
// The ring showcases our real mentors across all three communities. We build
// one pool per community, then interleave Zen · Rhythm · Cosmos so no two
// neighbours share a community (the drum wraps, so the last panel sits next to
// the first too).
type RingPanel = { src: string; name: string; craft?: string; focus?: string }
const zenRing: RingPanel[] = (communityPages.zen.people?.mentors ?? [])
  .filter((m) => m.photo?.startsWith('/img/mentor-') && m.name !== 'Ved Bhaskar')
  .map((m) => ({ src: m.photo!, name: m.name, craft: m.craft, focus: m.focus }))
const rhythmRing: RingPanel[] = (communityPages.rhythm.people?.mentors ?? [])
  .filter((m) => m.photo?.startsWith('/img/mentor-'))
  .map((m) => ({ src: m.photo!, name: m.name, craft: m.craft, focus: m.focus }))
const cosmosRing: RingPanel[] = cosmosMentors
  .filter((m) => m.name !== 'Acharya Poonam Gaur')
  .map((m) => ({ src: m.photo, name: m.name, craft: m.focus }))
const RING_PANELS: RingPanel[] = [
  zenRing[0], rhythmRing[0], cosmosRing[0],
  zenRing[1], rhythmRing[1], cosmosRing[1],
  zenRing[2], rhythmRing[2],
].filter(Boolean) as RingPanel[]

export function LifeCrafterRing({ dark = true }: ThemeProps) {
  const scope = useRef<HTMLElement>(null)
  const drum = useRef<HTMLDivElement>(null)
  const paused = useRef(false) // freeze the spin while a face is hovered, so names can be read
  const reduced = usePrefersReducedMotion()
  const mobile = useIsMobile()
  const NP = RING_PANELS.length
  // phones get a tighter drum so the panels don't overwhelm the small canvas
  const RADIUS = mobile ? 170 : 270
  const PANEL_W = mobile ? 122 : 190 // 5% narrower than before to make room for an extra panel
  const PANEL_H = mobile ? 168 : 260

  useGSAP(() => {
    if (reduced || !drum.current || !scope.current) return
    const el = drum.current
    const sec = scope.current
    let rot = 0
    let tilt = -12
    let tiltTarget = -12
    const onMove = (e: MouseEvent) => {
      const r = sec.getBoundingClientRect()
      const fy = (e.clientY - r.top) / r.height
      tiltTarget = (0.5 - fy) * 36 // mouse up tilts the drum up, down tilts down
    }
    sec.addEventListener('mousemove', onMove)
    const tick = (_t: number, dt: number) => {
      if (!paused.current) rot += (dt / 1000) * 12 // one turn every 30s; held while a face is hovered
      tilt += (tiltTarget - tilt) * 0.06
      el.style.transform = `rotateX(${tilt}deg) rotateY(${rot}deg)`
    }
    gsap.ticker.add(tick)
    return () => { sec.removeEventListener('mousemove', onMove); gsap.ticker.remove(tick) }
  }, { scope, dependencies: [reduced] })

  return (
    <section ref={scope} id="lifecrafters" className="relative overflow-hidden px-5 py-[12vh]">
      {/* the drum */}
      {reduced ? (
        <div className="container-lc flex flex-wrap justify-center gap-4">
          {RING_PANELS.slice(0, 6).map((p) => (
            <img key={p.name} src={p.src} alt={p.name} className="h-40 w-32 rounded-xl object-cover" />
          ))}
        </div>
      ) : (
        <div className="mx-auto w-full max-w-4xl" style={{ perspective: '1300px', height: mobile ? 230 : 340 }}>
          <div
            ref={drum}
            className="relative mx-auto h-full will-change-transform"
            style={{ width: PANEL_W, transformStyle: 'preserve-3d' }}
          >
            {RING_PANELS.map((p, i) => (
              <div
                key={p.name + i}
                className="group absolute left-0 top-1/2"
                onMouseEnter={() => { paused.current = true }}
                onMouseLeave={() => { paused.current = false }}
                style={{ marginTop: -PANEL_H / 2, width: PANEL_W, height: PANEL_H, transform: `rotateY(${(i / NP) * 360}deg) translateZ(${RADIUS}px)`, transformStyle: 'preserve-3d' }}
              >
                <div className="relative h-full w-full overflow-hidden rounded-xl shadow-xl ring-2 ring-white/15 transition-transform duration-300 group-hover:scale-[1.18]">
                  <img src={p.src} alt={p.name} className="h-full w-full object-cover" style={{ objectPosition: p.focus }} draggable={false} />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <p className="font-display text-[15px] leading-tight text-white">{p.name}</p>
                    <p className="mt-0.5 text-[10px] font-semibold leading-tight text-white/90">{p.craft}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* large mixed-typography lockup (reference style) */}
      <div className="container-lc mt-20 text-center">
        <p className={cn('font-sans text-[clamp(0.9rem,0.6rem+1vw,1.2rem)] font-bold uppercase tracking-[0.34em]', inkText(dark))}>
          Meet the LifeCrafters
        </p>
        <h2 className={cn('mt-3 font-sans text-[clamp(2.6rem,1rem+6vw,6rem)] font-semibold leading-[1.02] tracking-[-0.03em]', inkText(dark))}>
          <span className="inline-block bg-lime px-[0.18em] pb-[0.05em] text-[#141D12]">verified</span> instructors
        </h2>
        <p
          className={cn('mx-auto mt-2 text-[clamp(1.7rem,1rem+2.8vw,3.2rem)] italic leading-[1.1]', inkText(dark))}
          style={{ fontFamily: 'var(--font-body)', fontWeight: 350, fontStyle: 'italic' }}
        >
          to learn from. Not algorithms, not gurus.
        </p>
      </div>

      {/* trust & safety details */}
      <div className="container-lc mt-16">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {trust.map((t) => (
            <div key={t.q} className={cn('rounded-[22px] p-6', glassSoft(dark))}>
              <Icon name={t.icon} size={22} className={cn(dark ? 'text-lime' : 'text-ink')} />
              <h3 className={cn('mt-3 font-display text-xl font-normal tracking-[0.01em]', inkText(dark))}>{t.q}</h3>
              <p className={cn('mt-2 text-[15px] leading-relaxed', dark ? 'text-lc-ink/80' : 'text-lc-mute')}>{t.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  5 · EVENTS RAIL: pinned horizontal scroll of the latest events     */
/*  as frosted stat-style cards (max six).                             */
/* ------------------------------------------------------------------ */
const extraEvents = [
  { title: 'Evening Raag Circle', community: 'Rhythm', format: 'Online' as const, where: 'Live on LifeCraft4U', when: 'Fri · 7:00 PM', blurb: 'Sing the evening ragas together, all levels.' },
  { title: 'Chai & Chart Reading', community: 'Cosmos', format: 'In person' as const, where: 'Bengaluru', when: 'Sat · 5:00 PM', blurb: 'Bring your birth time. We bring the chai.' },
]
const hostFaces = ['/img/portrait-6.jpg', '/img/portrait-4.jpg', '/img/portrait-3.jpg', '/img/portrait-5.jpg', '/img/portrait-1.jpg', '/img/portrait-2.jpg']

const eventImgs = ['/img/gen/ev-photo-walk-group.jpg', '/img/gen/ev-full-moon-sit.jpg', '/img/gen/ev-riyaaz-circle.jpg', '/img/gen/ev-strength-meetup.jpg', '/img/gen/ev-raag-evening.jpg', '/img/gen/ev-chart-reading.jpg']

/* TEMPORARY: placeholder experiential-events section shown on the home page
   while the live events feed is empty. Remove with src/data/placeholderEvents.ts
   once the backend serves the real calendar (restore `return null` in
   EventsRail below). */
function PlaceholderEventsSection({ dark = true }: ThemeProps) {
  return (
    <PlaceholderEvents
      pinned
      railId="events"
      theme={dark ? 'dark' : 'light'}
      heading={{ kicker: "What's on", title: 'Experiences worth the trip.', intro: 'A first look at our experiential calendar. Register your interest for dates and booking.' }}
    />
  )
}

export function EventsRail({ dark = true }: ThemeProps) {
  // Only real backend events (cosmos/rhythm/zen). While the feed is loading
  // (null) or has none / is unreachable ([]), render nothing — never the
  // hand-authored samples.
  const live = useRecentEvents(6)
  // TEMP: until the backend serves the real calendar, show placeholder events
  // instead of nothing. Restore `return null` once live events exist.
  if (!live || live.length === 0) return <PlaceholderEventsSection dark={dark} />

  const railEvents = live.map((e, i) => ({
    key: e.id + ':' + i,
    id: e.id,
    title: e.title,
    community: e.community,
    format: e.format,
    where: e.where,
    when: e.when,
    blurb: e.blurb,
    image: e.image || eventImgs[i % eventImgs.length],
    host: e.hostPic || hostPhotoFor(e.hostName) || hostFaces[i % hostFaces.length],
    hostName: e.hostName,
    capacity: e.capacity,
    price: e.price,
    real: true,
  }))

  const cards = railEvents.map((e, i) => (
    <article
      key={e.key}
      data-rot={i % 2 ? 1 : -1}
      className={cn('ev-card relative flex h-[356px] w-[280px] shrink-0 flex-col overflow-hidden rounded-[24px] transition-[transform,opacity] duration-300 ease-brand sm:h-[392px] sm:w-[320px] sm:rounded-[28px] md:w-[360px]', glass(dark))}
      style={{ transform: `rotate(${i % 2 ? 1 : -1}deg)` }}
    >
      <div className="ev-card-img relative h-[154px] shrink-0 overflow-hidden">
        <img src={e.image} alt="" className="h-full w-full object-cover" loading="lazy" />
        {e.when && <span className={cn('label absolute left-3 top-3 rounded-pill bg-black/45 px-3 py-1.5 !text-[11px] text-white backdrop-blur')}>{e.when}</span>}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-3">
          <img src={e.host} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-white/30" />
          <div className="min-w-0">
            <p className={cn('truncate text-[13px] font-semibold', inkText(dark))}>{e.hostName || e.community}</p>
            <p className={cn('label !text-[11px]', muteText(dark))}>{e.hostName ? `${e.community} · ${e.format}` : e.format}</p>
          </div>
        </div>
        <h3 className={cn('mt-4 font-display text-[clamp(1.4rem,1.1rem+0.8vw,1.8rem)] font-normal leading-[1.05] tracking-[0.01em]', inkText(dark))}>
          {e.title}
        </h3>
        {e.blurb && <p className={cn('mt-2 text-[14px] leading-relaxed', muteText(dark))}>{e.blurb}</p>}
        <div className="mt-auto pt-4">
          <p className={cn('label mb-2.5 !text-[11px]', muteText(dark))}>
            {e.where}{e.capacity ? ` · ${e.capacity} seats` : ''}
          </p>
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="flex items-baseline gap-2">
                <span className={cn('text-[19px] font-extrabold', inkText(dark))}>{e.price ? `₹${e.price}` : 'Free'}</span>
                {!!e.price && <span className={cn('text-[12px]', muteText(dark))}>per seat</span>}
              </div>
            </div>
            <a href={eventWebappUrl(e.id)} className={cn('shrink-0 rounded-pill px-3.5 py-2 text-[13px] font-bold transition-transform duration-300 ease-brand hover:scale-[1.03] motion-reduce:hover:scale-100', dark ? 'bg-lime text-night' : 'bg-ink text-cream')}>Save a seat</a>
          </div>
        </div>
      </div>
    </article>
  ))

  const heading = (
    <RailHeading
      dark={dark}
      kicker="What's on"
      titleA="Real events, real"
      titleWord="seats"
      titleB="."
      intro="One-off gatherings, online or near you. Verified hosts, easy RSVP, and the chai is real too."
    />
  )

  // Three or fewer events: a simple centred row — no horizontal scroll and no
  // "View all" card. Those interactions come back automatically once the
  // backend serves more than three.
  if (railEvents.length <= 3) {
    return (
      <section id="events" className="px-5 py-[10vh]">
        <div className="container-lc">{heading}</div>
        <div className="container-lc mt-12 flex flex-wrap items-start justify-center gap-6">{cards}</div>
      </section>
    )
  }

  // More than three: the pinned horizontal rail, capped by a trailing "View
  // all" card (the scroll runs only as far as that card).
  cards.push(
    <a
      key="view-all"
      href="/communities"
      className={cn('ev-card group flex h-[356px] w-[280px] shrink-0 flex-col items-center justify-center gap-4 rounded-[24px] text-center transition-transform duration-300 ease-brand hover:-translate-y-1 sm:h-[392px] sm:w-[320px] sm:rounded-[28px] md:w-[360px]', glass(dark))}
    >
      <span className={cn('grid h-14 w-14 place-items-center rounded-full', dark ? 'bg-lime text-night' : 'bg-ink text-cream')}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      </span>
      <span className={cn('font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>View all events</span>
      <span className={cn('label !text-[11px]', muteText(dark))}>See the full calendar in the app</span>
    </a>,
  )

  return <PinnedCardRail id="events" heading={heading} cards={cards} />
}

/* Daily Rituals — a static, typographic "day arc" bento. Deliberately
   unlike the photographic EventsRail: no scroll-jacking, no images. Each
   tile glows in a time-of-day tone (warm at sunrise → cool at night),
   decoded by the gradient legend above the grid. Tiles are ordered by
   clock time so the section reads like a day unfolding. */
const RITUAL_ARC = [
  { title: 'Sunrise Yoga', glow: '#FF9E5C', span: 'lg:col-span-2' },
  { title: 'The Morning Stretch', glow: '#FFC24B', span: '' },
  { title: 'The Daily Sit', glow: '#A8D84B', span: '' },
  { title: 'Daily Consultation Window', glow: '#FF6B5C', span: '' },
  { title: 'Riyaaz Check-in', glow: '#9B6BFF', span: '' },
  { title: 'Evening Verse', glow: '#6D5BD0', span: 'lg:col-span-2' },
] as const

export function DailyRituals({ dark = true }: ThemeProps) {
  const reduced = usePrefersReducedMotion()
  const timeOf = (when: string) => (when.split('·').pop() || '').trim().replace(' to ', '–')

  return (
    <section id="programs" className="relative px-5 py-24 md:py-32">
      <div className="container-lc">
        <RailHeading
          dark={dark}
          kicker="Live, every day"
          titleA="Daily programs to"
          titleWord="tune into"
          titleB="."
          intro="Live broadcasts and drop-in sessions threaded through the day, from first light to last: morning yoga, guided sits, riyaaz check-ins. Tune in anytime; no streak to protect, just a rhythm worth keeping."
        />

        {/* day-arc legend: colour encodes time of day, warm → cool */}
        <div className="mx-auto mt-9 max-w-2xl">
          <div className="h-1.5 w-full rounded-full" style={{ background: 'linear-gradient(90deg,#FF9E5C,#FFC24B,#A8D84B,#FF6B5C,#9B6BFF,#6D5BD0)' }} />
          <div className={cn('mt-2 flex justify-between text-[11px] font-semibold uppercase tracking-[0.18em]', muteText(dark))}>
            <span>Sunrise</span>
            <span>Midday</span>
            <span>Evening</span>
            <span>Night</span>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RITUAL_ARC.map((o, i) => {
            const p = programs.find((x) => x.title === o.title)
            if (!p) return null
            return (
              <motion.article
                key={o.title}
                initial={reduced ? false : { opacity: 0, y: 24 }}
                whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className={cn('group relative flex min-h-[220px] flex-col overflow-hidden rounded-[24px] p-6 lg:min-h-[240px]', o.span, glass(dark))}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-40 blur-2xl transition-opacity duration-500 group-hover:opacity-70"
                  style={{ background: o.glow }}
                />
                <div className="relative flex items-start justify-between gap-4">
                  <span className={cn('font-display text-[clamp(1.6rem,1.2rem+1vw,2.2rem)] font-normal leading-none tracking-[0.01em]', inkText(dark))}>
                    {timeOf(p.when)}
                  </span>
                  <span className={cn('mt-1 inline-flex items-center gap-1.5 rounded-pill bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]', muteText(dark))}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: o.glow }} />
                    {p.community}
                  </span>
                </div>
                <div className="relative mt-auto pt-6">
                  <h3 className={cn('font-display text-[clamp(1.3rem,1.05rem+0.7vw,1.7rem)] font-normal leading-[1.05]', inkText(dark))}>{p.title}</h3>
                  <p className={cn('mt-2 max-w-[46ch] text-[14px] leading-relaxed', muteText(dark))}>{p.blurb}</p>
                  <p className={cn('label mt-4 !text-[11px]', muteText(dark))}>{p.where} · drop in anytime</p>
                </div>
              </motion.article>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  5.4 · EXPANDABLE VIDEO: the community intro sits beside the hero   */
/*  copy as a card; one button maximises it into a lightbox, another   */
/*  (or Escape / backdrop) brings it back down.                        */
/* ------------------------------------------------------------------ */
export function ExpandableVideoCard({
  videoSrc,
  poster,
  label = 'Community intro · demo video',
  children,
}: {
  videoSrc: string
  poster: string
  label?: string
  children?: React.ReactNode
}) {
  const [max, setMax] = useState(false)

  useEffect(() => {
    if (!max) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMax(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [max])

  return (
    <>
      <div className="lc-glass relative overflow-hidden rounded-card">
        <div className="relative">
          <video
            src={videoSrc}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            className="aspect-video w-full object-cover motion-reduce:hidden"
          />
          <img src={poster} alt="" className="hidden aspect-video w-full object-cover motion-reduce:block" />
          <span className="label absolute left-3 top-3 rounded-pill bg-black/50 px-2.5 py-1 !text-[11px] text-white backdrop-blur">{label}</span>
          <button
            onClick={() => setMax(true)}
            aria-label="Maximise video"
            className="absolute bottom-3 right-3 grid h-10 w-10 place-items-center rounded-full bg-black/50 text-white backdrop-blur transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </button>
        </div>
        {children}
      </div>

      <AnimatePresence>
        {max && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-night-900/90 p-4 backdrop-blur-sm md:p-10"
            onClick={() => setMax(false)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 12 }}
              transition={{ type: 'spring', stiffness: 280, damping: 26 }}
              className="relative w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <video src={videoSrc} poster={poster} autoPlay muted loop playsInline controls className="max-h-[80vh] w-full rounded-card object-contain shadow-pop" />
              <button
                onClick={() => setMax(false)}
                aria-label="Minimise video"
                className="absolute -top-4 right-0 grid h-11 w-11 -translate-y-full place-items-center rounded-full bg-white/15 text-white backdrop-blur transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 3v6H3M15 21v-6h6M3 9l7-7M21 15l-7 7" />
                </svg>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  5.45 · VIDEO PANEL: three hero-video layouts for community pages.  */
/*  'single' (16:9 card), 'reels' (9:16 collage of four), 'carousel'   */
/*  (16:9, auto-advancing). All muted in the header; sound and         */
/*  controls arrive only in the expanded lightbox.                     */
/* ------------------------------------------------------------------ */
export type PanelVideo = { src: string; poster: string }
export type VideoLayout = 'single' | 'reels' | 'carousel'

export function VideoPanel({
  layout = 'single',
  videos,
  label = 'Community intro · demo video',
}: {
  layout?: VideoLayout
  videos: PanelVideo[]
  label?: string
}) {
  const reduced = usePrefersReducedMotion()
  const [open, setOpen] = useState<number | null>(null)
  const [idx, setIdx] = useState(0)

  /* Muted-autoplay isn't always honoured on mount (esp. after a lazy swap),
     which would leave the poster/blank tile showing. Nudge play() once the
     element can, so tiles reliably come alive. */
  const autoPlayRef = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return
    const play = () => { void el.play().catch(() => {}) }
    play()
    el.addEventListener('loadeddata', play, { once: true })
    el.addEventListener('canplay', play, { once: true })
  }, [])

  /* Lazy-load: hold off mounting the autoplay <video> tags (and their
     network fetch) until the panel scrolls near the viewport. Posters
     show meanwhile, so an off-screen reels grid costs nothing until seen. */
  const inViewRef = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = inViewRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true)
          io.disconnect()
        }
      },
      { rootMargin: '300px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  /* carousel auto-advance, paused while the lightbox is up */
  useEffect(() => {
    if (layout !== 'carousel' || reduced || open !== null || videos.length < 2) return
    const t = window.setInterval(() => setIdx((v) => (v + 1) % videos.length), 5000)
    return () => window.clearInterval(t)
  }, [layout, reduced, open, videos.length])

  useEffect(() => {
    if (open === null) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const expandIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  )
  const lightbox = (
    <AnimatePresence>
      {open !== null && videos[open] && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-night-900/90 p-4 backdrop-blur-sm md:p-10"
          onClick={() => setOpen(null)}
        >
          <motion.div
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 12 }}
            transition={{ type: 'spring', stiffness: 280, damping: 26 }}
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* sound lives here, and only here */}
            <video
              key={open}
              src={videos[open].src}
              poster={videos[open].poster}
              autoPlay
              loop
              playsInline
              controls
              className="max-h-[80vh] w-full rounded-card object-contain shadow-pop"
            />
            <button
              onClick={() => setOpen(null)}
              aria-label="Minimise video"
              className="absolute -top-4 right-0 grid h-11 w-11 -translate-y-full place-items-center rounded-full bg-white/15 text-white backdrop-blur transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 3v6H3M15 21v-6h6M3 9l7-7M21 15l-7 7" />
              </svg>
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  if (layout === 'reels') {
    /* Two videos get a bigger, more deliberate presentation: stacked on
       mobile, side-by-side with a gentle stagger on wider screens. Three
       or four fall back to the compact 2×N collage. */
    const two = videos.length === 2
    return (
      <>
        <div
          ref={inViewRef}
          className={cn(
            'relative mx-auto w-full',
            two ? 'max-w-[360px] sm:max-w-[480px] lg:max-w-[520px]' : 'max-w-[300px] sm:max-w-sm lg:max-w-[320px]',
          )}
        >
          <div className={cn('grid', two ? 'grid-cols-2 gap-3 sm:gap-6' : 'grid-cols-2 gap-3')}>
            {(two ? videos : videos.slice(0, 4)).map((v, i) => (
              <button
                key={i}
                onClick={() => setOpen(i)}
                aria-label={`Play video ${i + 1}`}
                className={cn(
                  'lc-glass group relative aspect-[9/16] overflow-hidden bg-night-900 transition-transform duration-300 ease-brand hover:-translate-y-1',
                  two ? 'rounded-[26px] shadow-pop sm:[&:nth-child(2)]:mt-10' : 'rounded-[20px]',
                )}
              >
                {/* poster always sits behind, so a tile is never blank while
                    the video mounts / autoplay negotiates */}
                <img src={v.poster} alt="" className="absolute inset-0 h-full w-full object-cover" />
                {inView && !reduced && (
                  <video
                    ref={autoPlayRef}
                    src={v.src}
                    poster={v.poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
                <span
                  className={cn(
                    'absolute grid place-items-center rounded-full bg-black/50 text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100',
                    two ? 'bottom-3 right-3 h-9 w-9' : 'bottom-2.5 right-2.5 h-7 w-7',
                  )}
                  aria-hidden="true"
                >
                  {expandIcon}
                </span>
              </button>
            ))}
          </div>
        </div>
        {lightbox}
      </>
    )
  }

  if (layout === 'carousel') {
    const v = videos[Math.min(idx, videos.length - 1)]
    return (
      <>
        <div ref={inViewRef} className="lc-glass relative overflow-hidden rounded-card">
          <div className="relative aspect-video">
            {inView && !reduced ? (
              <AnimatePresence initial={false}>
                <motion.video
                  key={idx}
                  ref={autoPlayRef}
                  src={v.src}
                  poster={v.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
            ) : (
              <img src={v.poster} alt="" className="absolute inset-0 h-full w-full object-cover" />
            )}
          </div>
          <span className="label absolute left-3 top-3 rounded-pill bg-black/50 px-2.5 py-1 !text-[11px] text-white backdrop-blur">{label}</span>
          <button
            onClick={() => setOpen(idx)}
            aria-label="Maximise video"
            className="absolute bottom-3 right-3 grid h-10 w-10 place-items-center rounded-full bg-black/50 text-white backdrop-blur transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
          >
            {expandIcon}
          </button>
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
            {videos.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Show video ${i + 1}`}
                className={cn('h-1.5 rounded-pill transition-all duration-300', i === idx ? 'w-6 bg-lime' : 'w-1.5 bg-white/50 hover:bg-white/80')}
              />
            ))}
          </div>
        </div>
        {lightbox}
      </>
    )
  }

  const v0 = videos[0]
  return (
    <>
      <div ref={inViewRef} className="lc-glass relative overflow-hidden rounded-card">
        {inView && !reduced ? (
          <video ref={autoPlayRef} src={v0.src} poster={v0.poster} autoPlay muted loop playsInline preload="metadata" className="aspect-video w-full object-cover" />
        ) : (
          <img src={v0.poster} alt="" className="aspect-video w-full object-cover" />
        )}
        <span className="label absolute left-3 top-3 rounded-pill bg-black/50 px-2.5 py-1 !text-[11px] text-white backdrop-blur">{label}</span>
        <button
          onClick={() => setOpen(0)}
          aria-label="Maximise video"
          className="absolute bottom-3 right-3 grid h-10 w-10 place-items-center rounded-full bg-black/50 text-white backdrop-blur transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
        >
          {expandIcon}
        </button>
      </div>
      {lightbox}
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  5.48 · PARTNER CALLOUT: the pitch to future partners. Stands in    */
/*  for the marketplace until real partners are aboard; the grid       */
/*  returns behind it later.                                           */
/* ------------------------------------------------------------------ */
const PARTNER_ICONS: IconName[] = ['users', 'calendar', 'lock', 'heart']

export function PartnerCallout({
  dark = true,
  accent = '#C8F04B',
  categories,
}: {
  dark?: boolean
  accent?: string
  categories: string[]
}) {
  const items = [...categories, ...categories, ...categories, ...categories]
  return (
    <section id="partners" className="relative px-5 py-[8vh]">
      <div className="container-lc">
        <div className={cn('relative overflow-hidden rounded-[32px]', glass(dark))}>
          <span className="blob absolute -left-20 -top-20 h-64 w-64 opacity-30" style={{ background: accent }} aria-hidden="true" />
          <span className="blob absolute -bottom-24 -right-16 h-72 w-72 opacity-20" style={{ background: '#FF6B5C' }} aria-hidden="true" />

          <div className="relative px-8 pb-8 pt-10 text-center md:px-12">
            <p className={cn('label', muteText(dark))}>Alliances & partners</p>
            <h2 className={cn('mx-auto mt-4 max-w-[22ch] font-display text-[clamp(2rem,1.2rem+3.4vw,3.8rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
              Built something worth sharing? Put it in front of people who{' '}
              <span className="inline-block -rotate-1 rounded-[0.24em] bg-lime px-[0.18em] pb-[0.05em] text-[#141D12]">show up</span>.
            </h2>
          </div>

          {/* the kinds of partners we're looking for, on a slow loop */}
          <div
            className={cn('relative overflow-hidden border-y py-4', dark ? 'border-white/10' : 'border-ink/10')}
            style={{ ['--marquee-duration' as string]: '26s' }}
          >
            <div className="motion-safe:animate-marquee-x flex w-max items-center gap-7 motion-reduce:w-full motion-reduce:flex-wrap motion-reduce:justify-center">
              {items.map((c, i) => (
                <span key={i} className="flex items-center gap-7 whitespace-nowrap">
                  <span className={cn('font-display text-2xl font-normal tracking-[0.01em]', i % 2 ? inkText(dark) : muteText(dark))}>{c}</span>
                  <span style={{ color: accent }} aria-hidden="true">✦</span>
                </span>
              ))}
            </div>
          </div>

          <div className="relative p-8 md:p-12">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {partnerWhy.points.map((pt, i) => (
                <div key={pt} className={cn('rounded-[20px] p-5', glassSoft(dark))}>
                  <span className={cn('grid h-10 w-10 place-items-center rounded-2xl', glass(dark))} style={{ color: dark ? '#C8F04B' : accent }}>
                    <Icon name={PARTNER_ICONS[i % PARTNER_ICONS.length]} size={18} />
                  </span>
                  <p className={cn('mt-4 text-[14px] font-medium leading-relaxed', inkText(dark))}>{pt}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col items-center gap-3.5">
              <MagneticButton href="/contact" className="rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-8 xl:py-4 xl:text-[16px] font-bold text-night">
                {partnerWhy.cta}
              </MagneticButton>
              <p className={cn('label !text-[11px]', muteText(dark))}>
                The partner marketplace opens here once our first alliances are aboard
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  5.5 · MEMBERSHIP NOTE: placeholder for the subscription model.     */
/*  Joining stays free; a small monthly membership will unlock full    */
/*  interaction. Copy is a stand-in until pricing is real.             */
/* ------------------------------------------------------------------ */
export function MembershipNote({ dark = true }: ThemeProps) {
  return (
    <section className="relative px-5 pt-[6vh]">
      <div className="container-lc">
        <div className={cn('relative overflow-hidden rounded-[32px] p-8 md:p-12', glass(dark))}>
          <span className="blob absolute -right-14 -top-14 h-56 w-56 opacity-30" style={{ background: '#3B5BFE' }} aria-hidden="true" />
          <div className="relative grid gap-8 md:grid-cols-12 md:items-center">
            <div className="md:col-span-8">
              <p className={cn('label flex items-center gap-2.5', muteText(dark))}>
                Membership · coming soon
                <span className={cn('rounded-pill px-2.5 py-0.5 !text-[11px]', glassSoft(dark))}>placeholder</span>
              </p>
              <h2 className={cn('mt-4 font-display text-[clamp(1.7rem,1.1rem+2.2vw,2.8rem)] font-normal leading-[1.05] tracking-[0.01em]', inkText(dark))}>
                Free to join. A small monthly membership to go deeper.
              </h2>
              <p className={cn('mt-4 max-w-xl text-[15px] xl:text-[16px] leading-relaxed', muteText(dark))}>
                Joining a community will always be free: read the daily practice, browse events,
                sit in on open circles. A gentle monthly membership will unlock the full life of a
                community: conversations and replies, member circles, discounts on events and 1-1 programs.
              </p>
            </div>
            <div className="md:col-span-4">
              <div className={cn('rounded-[22px] p-6', glassSoft(dark))}>
                {[
                  ['Join any community', 'Free, always'],
                  ['Daily readings & open circles', 'Free, always'],
                  ['Conversations, circles, discounts', 'Membership'],
                ].map(([a, b]) => (
                  <div key={a} className="flex items-baseline justify-between gap-4 border-b border-white/8 py-2.5 last:border-0">
                    <span className={cn('text-[14px] font-medium', inkText(dark))}>{a}</span>
                    <span className={cn('label shrink-0 !text-[11px]', b === 'Membership' ? 'text-lc-accent' : muteText(dark))}>{b}</span>
                  </div>
                ))}
                <p className={cn('label mt-4 !text-[11px]', muteText(dark))}>Pricing lands with launch</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  6 · CLOSING CTA (from Option D) + FOOTER                           */
/* ------------------------------------------------------------------ */
const CTA_PHOTOS = [
  { src: '/img/gen/cta-music-duo.jpg', cls: 'left-[3%] top-[11%] w-32 -rotate-6 md:w-44', d: '0s', ar: '3 / 2' },
  { src: '/img/gen/cta-street-camera.jpg', cls: 'right-[4%] top-[8%] w-24 rotate-3 md:w-32', d: '1.1s', ar: '4 / 5' },
  { src: '/img/gen/cta-photo-friends.jpg', cls: 'bottom-[11%] left-[7%] w-24 rotate-6 md:w-32', d: '2s', ar: '4 / 5' },
  { src: '/img/gen/cta-friends-outdoor.jpg', cls: 'bottom-[8%] right-[6%] w-32 -rotate-3 md:w-44', d: '0.6s', ar: '3 / 2' },
]

export function ClosingGlassCTA({ dark = true }: ThemeProps) {
  const reduced = usePrefersReducedMotion()
  return (
    <section id="join" className="relative px-5 py-[12vh]">
      <div className="container-lc">
        <div className={cn('relative overflow-hidden rounded-[36px] px-6 py-20 text-center md:py-28', glass(dark))}>
          <span className="blob absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 opacity-40" style={{ background: '#7C3AED' }} aria-hidden="true" />
          <span className="blob absolute -bottom-16 right-[12%] h-56 w-56 opacity-25" style={{ background: '#FF6B5C' }} aria-hidden="true" />

          {/* drifting photos: the hero collage coming back to wave goodbye */}
          <div className="pointer-events-none absolute inset-0 hidden sm:block" aria-hidden="true">
            {CTA_PHOTOS.map((p) => (
              <img
                key={p.src}
                src={p.src}
                alt=""
                loading="lazy"
                className={cn(
                  'absolute rounded-2xl object-cover opacity-80 shadow-xl ring-2',
                  dark ? 'ring-white/15' : 'ring-ink/10',
                  p.cls,
                  !reduced && 'motion-safe:animate-float',
                )}
                style={{ animationDelay: p.d, animationDuration: '8s', aspectRatio: p.ar }}
              />
            ))}
          </div>

          <div className="relative">
            <h2 className={cn('mx-auto max-w-[16ch] font-display text-[clamp(2.4rem,1.2rem+6vw,5.4rem)] font-normal leading-[0.98] tracking-[0.01em]', inkText(dark))}>
              Old friends you haven't{' '}
              <span className="inline-block -rotate-1 rounded-[0.24em] bg-lime px-[0.18em] pb-[0.06em] text-[#141D12]">met yet</span>.
            </h2>
            <p className={cn('mx-auto mt-6 max-w-md text-xl', muteText(dark))}>Bring an interest. Leave with people.</p>
            <MagneticButton
              href="/communities"
              data-cursor="join"
              className={cn('mt-9 rounded-pill px-6 py-3 text-[15px] xl:px-9 xl:py-4 xl:text-[18px] font-bold', dark ? 'bg-lime text-night' : 'bg-ink text-cream')}
            >
              Find your community
            </MagneticButton>
            <p className={cn('label mt-6', muteText(dark))}>No points. No streaks. No scoreboard.</p>
          </div>
        </div>
      </div>
    </section>
  )
}

const FOOTER_MENU = [
  { to: '/communities', label: 'Communities' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/lifecrafter-program', label: 'LifeCrafter Program' },
]
const FOOTER_LEGAL = [
  { to: '/privacy-policy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Use' },
  { to: '/instructor-policy', label: 'Instructor Policy' },
]

function FooterColumn({ dark, heading, links }: { dark: boolean; heading: string; links: { to: string; label: string }[] }) {
  return (
    <div className="md:col-span-3">
      <h4 className={cn('label', muteText(dark))}>{heading}</h4>
      <ul className="mt-4 space-y-2.5">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className={cn('text-[15px] transition-colors', muteText(dark), dark ? 'hover:text-cream' : 'hover:text-ink')}>{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function SiteFooter({ dark = true }: ThemeProps) {
  return (
    <footer className="relative px-5 pb-10">
      <div className="container-lc">
        <div className={cn('rounded-[32px] p-8 md:p-12', dark ? 'lc-navbar' : glass(dark))}>
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-6">
              <Logo size={28} tone={dark ? 'white' : 'ink'} />
              <p className={cn('mt-4 max-w-xs font-display text-lg font-normal tracking-[0.01em]', muteText(dark))}>{brand.tagline}</p>
            </div>
            <FooterColumn dark={dark} heading="Explore" links={FOOTER_MENU} />
            <FooterColumn dark={dark} heading="Legal" links={FOOTER_LEGAL} />
          </div>
          <div className={cn('mt-10 flex flex-col gap-3 border-t pt-6 label sm:flex-row sm:items-center sm:justify-between', dark ? 'border-white/10' : 'border-ink/10', muteText(dark))}>
            <p>© 2026 LifeCraft4U</p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <a href="mailto:info@lifecraft4u.com" className={cn('transition-colors', dark ? 'hover:text-white' : 'hover:text-ink')}>
                info@lifecraft4u.com
              </a>
              <a href="https://wa.me/918796790333" target="_blank" rel="noopener noreferrer" className={cn('inline-flex items-center gap-1.5 transition-colors', dark ? 'hover:text-white' : 'hover:text-ink')}>
                <Icon name="whatsapp" size={14} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
